import { TrackChangesParser } from '../../word/utils/TrackChangesParser';
import { paragraphXmlHasProtectedMarkup } from './protectedRegions';

/**
 * Fold live paragraph text and the accepted apply target the same way so
 * post-apply checks do not treat a trailing paragraph mark or extra spaces
 * as a real mismatch — and still catch a garbled insert (`…מי מהם.למנורה),.`).
 */

export function foldApplyText(text: string): string {
  return (text || '')
    .replace(/[\r\n\u0007\u0005\u0008]+/g, '')
    .replace(/\s+/g, ' ')
    .trim();
}

/**
 * All Markup (`Range.text`) still contains deleted runs. Remove each deleted
 * segment in document order to get the accepted view of THIS paragraph.
 */
export function acceptedTextFromVisibleAndDeletes(visible: string, deletedTexts: string[]): string {
  let accepted = visible || '';
  for (const raw of deletedTexts) {
    const del = (raw || '').replace(/[\r\n\u0007\u0005\u0008]+/g, '');
    if (!del) continue;
    const idx = accepted.indexOf(del);
    if (idx >= 0) {
      accepted = accepted.slice(0, idx) + accepted.slice(idx + del.length);
    }
  }
  return accepted;
}

/**
 * After a native track-changes apply, `Range.text` is All Markup (inserts +
 * still-present deletions). Verify against accepted OOXML (skip `w:del`) so
 * 245 + two 8-char inserts is not treated as 261 ≠ 245.
 */
export function acceptedTextFromParagraphXml(
  pXml: string | null | undefined,
  fallbackVisible: string,
): string {
  if (!pXml?.trim()) return fallbackVisible;
  const accepted = TrackChangesParser.extractAcceptedText(pXml);
  return accepted || fallbackVisible;
}

/**
 * Word Mac/Win32 `getOoxml()` can wrap the target `<w:p>` with siblings.
 * Pick the paragraph whose accepted/surface text is closest to the live
 * visible string so verify does not read a leftover 88-char CR cell.
 */
export function pickBestParagraphXmlFromPackage(
  packageXml: string | null | undefined,
  fallbackVisible: string,
): string {
  if (!packageXml?.trim()) return '';
  const ns = 'http://schemas.openxmlformats.org/wordprocessingml/2006/main';
  try {
    const doc = new DOMParser().parseFromString(packageXml, 'text/xml');
    if (doc.querySelector('parsererror')) return packageXml;
    let nodes: HTMLCollectionOf<Element> | NodeListOf<Element> = doc.getElementsByTagNameNS(ns, 'p');
    if (nodes.length === 0) {
      nodes = doc.getElementsByTagName('w:p');
    }
    if (nodes.length <= 1) {
      return nodes.length === 1 ? new XMLSerializer().serializeToString(nodes.item(0) as Element) : packageXml;
    }
    const visibleFold = foldApplyText(fallbackVisible);
    const tail = visibleFold.slice(-40);
    const serializer = new XMLSerializer();
    let bestXml = '';
    let bestScore = Number.NEGATIVE_INFINITY;
    for (let i = 0; i < nodes.length; i++) {
      const el = nodes.item(i) as Element;
      const pXml = serializer.serializeToString(el);
      const accepted = foldApplyText(TrackChangesParser.extractAcceptedText(pXml));
      const surface = foldApplyText(el.textContent || '');
      let score = 0;
      if (accepted === visibleFold || surface === visibleFold) score += 1000;
      if (tail.length >= 16 && (accepted.includes(tail) || surface.includes(tail))) score += 200;
      if (acceptedExtractLooksUnusable(fallbackVisible, accepted || surface)) score -= 500;
      const len = (accepted || surface).length;
      score += Math.max(0, 120 - Math.abs(len - visibleFold.length));
      if (score > bestScore) {
        bestScore = score;
        bestXml = pXml;
      }
    }
    return bestXml || packageXml;
  } catch {
    return packageXml;
  }
}

export function acceptedTextFromOoxmlPackage(
  packageXml: string | null | undefined,
  fallbackVisible: string,
): string {
  if (!packageXml?.trim()) return fallbackVisible;
  const picked = pickBestParagraphXmlFromPackage(packageXml, fallbackVisible);
  const accepted = acceptedTextFromParagraphXml(picked || packageXml, fallbackVisible);
  return acceptedExtractLooksUnusable(fallbackVisible, accepted) ? fallbackVisible : accepted;
}

export function foldedApplyMatchesTarget(liveVisible: string, cleanTarget: string): boolean {
  const live = foldApplyText(liveVisible);
  const target = foldApplyText(cleanTarget);
  if (!target) return false;
  return live === target;
}

/**
 * `paragraph.getOoxml()` sometimes yields a sibling leftover (88-char CR).
 * That extract must not fail incremental apply or trigger a whole-paragraph replace.
 */
export function acceptedExtractLooksUnusable(visible: string, accepted: string): boolean {
  const live = foldApplyText(visible);
  const got = foldApplyText(accepted);
  if (!live) return false;
  if (!got) return true;
  if (got.length < Math.floor(live.length * 0.5)) return true;
  return applyTargetLooksTruncated(live, got);
}

/**
 * True when `cleanTarget` is only a head fragment of the live clause (typical
 * extractAcceptedText / incomplete AI XML). Applying it deletes the rest of
 * the paragraph — the `…מי מהם.למנורה),.` corruption.
 */
function sharedPrefixLength(a: string, b: string): number {
  let shared = 0;
  while (shared < a.length && shared < b.length && a[shared] === b[shared]) {
    shared += 1;
  }
  return shared;
}

function targetHasPunctuationGarbageTail(target: string): boolean {
  const trimmed = target.replace(/\s+$/, '');
  const lastFew = trimmed.slice(-6);
  if (/[,;]\s*[.,;)\]\}]/.test(lastFew)) return true;
  if (/[)\]\}]\s*[,;]\s*\.?$/.test(trimmed)) return true;
  return false;
}

/** Ends with sentence punctuation (optional closing quote), not a broken `),.`-style tail. */
function targetHasCleanSentenceEnding(target: string): boolean {
  if (target.length < 16) return false;
  if (targetHasPunctuationGarbageTail(target)) return false;
  const trimmed = target.replace(/\s+$/, '');
  const last = trimmed.slice(-1);
  if (last === '.' || last === '!' || last === '?') return true;
  const closingQuotes = ['"', "'", '\u201D', '\u2019', '\u05F4', '\u05F3'];
  if (closingQuotes.includes(last)) {
    const beforeLast = trimmed.slice(-2, -1);
    if (beforeLast === '.' || beforeLast === '!' || beforeLast === '?') return true;
  }
  return false;
}

/** Same `סעיף N:` header but a different full instruction (25.4 delete → replace). */
function looksLikeSameSectionInstructionRewrite(baseline: string, target: string): boolean {
  const sectionRef = /^(סעיף\s+[\d.]+(?:\s*\([^)]*\))?)\s*:/;
  const baseMatch = baseline.match(sectionRef);
  const targetMatch = target.match(sectionRef);
  if (!baseMatch || !targetMatch) return false;
  if (foldApplyText(baseMatch[1]) !== foldApplyText(targetMatch[1])) return false;
  return targetHasCleanSentenceEnding(target);
}

/**
 * Target is almost entirely a prefix of the live paragraph — applying deletes
 * the rest (`…יועצי הפרויקט` dropped) or leaves `),.`-style garbage.
 */
function targetLooksLikeBrokenHeadFragment(baseline: string, target: string): boolean {
  if (baseline.startsWith(target) && baseline.length > target.length + 16) return true;
  const shared = sharedPrefixLength(baseline, target);
  if (shared >= 40 && shared >= Math.floor(target.length * 0.85)) {
    if (baseline.length - target.length >= 16) return true;
  }
  if (targetHasPunctuationGarbageTail(target) && shared >= 40) return true;
  return false;
}

export function applyTargetLooksTruncated(baselineVisible: string, cleanTarget: string): boolean {
  const baseline = foldApplyText(baselineVisible);
  const target = foldApplyText(cleanTarget);
  if (!baseline || !target) return false;
  if (target.length >= Math.floor(baseline.length * 0.7)) return false;
  const tailNeedle = baseline.slice(-40).trim();
  if (tailNeedle.length >= 16 && target.includes(tailNeedle.slice(0, 20))) return false;
  if (targetLooksLikeBrokenHeadFragment(baseline, target)) return true;
  if (targetHasCleanSentenceEnding(target)) return false;
  if (looksLikeSameSectionInstructionRewrite(baseline, target)) return false;
  return true;
}

/**
 * If the suggestion is only a rewritten head, keep the live clause tail after the
 * shared prefix so apply cannot delete `יועצי הפרויקט או מי מהם`.
 */
export function extendTruncatedApplyTarget(baselineVisible: string, cleanTarget: string): string {
  const baseline = foldApplyText(baselineVisible);
  const target = foldApplyText(cleanTarget);
  if (!applyTargetLooksTruncated(baseline, target)) return target;
  let shared = 0;
  while (shared < baseline.length && shared < target.length && baseline[shared] === target[shared]) {
    shared += 1;
  }
  if (shared < 40) return target;
  const leftover = target.slice(shared);
  // Truncated CR extracts often end with `),.` garbage. Splicing that onto the
  // live tail produced `…מנורה),. ניתוח` (245→247) and an insert of `,`.
  if (/^[\s,.;:)\]\}]+$/.test(leftover)) {
    return target;
  }
  return foldApplyText(target + baseline.slice(shared));
}

function isolateMatchingParagraphXml(packageXml: string, visibleBaseline: string): string {
  const visible = foldApplyText(visibleBaseline);
  if (!visible) return '';
  const blocks = packageXml.match(/<(?:\w+:)?p\b[\s\S]*?<\/(?:\w+:)?p>/gi) || [];
  let best = '';
  let bestScore = -1;
  for (const frag of blocks) {
    const text = foldApplyText(
      frag
        .replace(/<[^>]+>/g, '')
        .replace(/&amp;/g, '&')
        .replace(/&lt;/g, '<')
        .replace(/&gt;/g, '>')
        .replace(/&quot;/g, '"'),
    );
    if (!text) continue;
    let score = 0;
    if (text === visible) score += 1000;
    const tail = visible.slice(-40);
    if (tail.length >= 16 && text.includes(tail)) score += 200;
    score += Math.max(0, 120 - Math.abs(text.length - visible.length));
    if (score > bestScore) {
      bestScore = score;
      best = frag;
    }
  }
  return bestScore >= 200 ? best : '';
}

/**
 * Whether the live target paragraph needs the Office.js protected-region walk.
 * Unknown / unisolated packages return true (scan). A clean isolated `<w:p>`
 * returns false so apply can skip ~1.7s of empty field/bookmark/comment loads.
 */
export function ooxmlParagraphHasProtectedMarkup(
  packageXml: string | null | undefined,
  visibleBaseline: string,
): boolean {
  if (!packageXml?.trim()) return true;
  let picked = '';
  try {
    picked = pickBestParagraphXmlFromPackage(packageXml, visibleBaseline);
  } catch {
    picked = '';
  }
  if (picked && !/<(?:\w+:)?(?:package|body)\b/i.test(picked)) {
    return paragraphXmlHasProtectedMarkup(picked);
  }
  const isolated = isolateMatchingParagraphXml(packageXml, visibleBaseline);
  if (!isolated) return true;
  return paragraphXmlHasProtectedMarkup(isolated);
}

/**
 * Locate the live paragraph, not the AI destination. Searching for `newText`
 * before apply misses the cheap live-text scan (8.2: 202 is not in the 250
 * baseline) and falls through to `body.getOoxml()` of the whole document.
 * Use `newText` only when the displayed CR is a truncated leftover extract.
 */
/**
 * QR suffixes must append to the live paragraph. The card CR can be a shorter
 * comment on the same clause (track_changes_3: 172 vs live 207 + comment mark).
 * Applying CR+suffix would delete the extra live wording.
 */
export function retargetQrSuffixToLiveParagraph(liveText: string, suggestedText: string): string {
  const live = foldApplyText(liveText);
  const suggested = foldApplyText(suggestedText);
  if (!live) return suggested;
  // Normal QR: "... [ראה תיקון מוצע]"
  const trailing = suggested.match(/ (\[[^\]]+\])$/);
  if (trailing) {
    const suffix = trailing[0];
    if (live.endsWith(suffix) || live.endsWith(suffix.trim())) return live;
    return `${live}${suffix}`;
  }
  // All-deleted CR + appended QR: accepted text is only the bracket (w:del excluded).
  const onlyBracket = suggested.match(/^\[([^\]]+)\]$/);
  if (onlyBracket) {
    const suffix = ` [${onlyBracket[1]}]`;
    if (live.endsWith(suffix) || live.endsWith(suffix.trim())) return live;
    return `${live}${suffix}`;
  }
  return suggested;
}

export function pickSubmitLocateSearchText(
  clientRequestPlainText: string,
  newText: string,
  fallback = '',
): string {
  const cr = foldApplyText(clientRequestPlainText);
  const target = foldApplyText(newText);
  const fb = foldApplyText(fallback);
  const withoutSuffix = target.replace(/ \[[^\]]+\]$/, '').trim();
  const isQrAppend =
    (Boolean(withoutSuffix) && withoutSuffix !== target) || /^\[([^\]]+)\]$/.test(target);
  const usable = (text: string) =>
    text.length >= 8 && !/^\[?ראה תיקון מוצע\]?$/.test(text);
  if (isQrAppend) {
    if (usable(cr)) return cr;
    if (usable(withoutSuffix) && withoutSuffix !== target) return withoutSuffix;
    if (usable(fb)) return fb;
    return '';
  }
  const crLooksTruncated = applyTargetLooksTruncated(target, cr);
  if (usable(cr) && cr.length >= 40 && !crLooksTruncated) return cr;
  if (usable(target) && target.length >= 40) return target;
  if (usable(cr)) return cr;
  if (usable(fb)) return fb;
  return '';
}
